import React, { useState, useEffect, useRef, useMemo } from "react";
import { X, ChevronDown, Check, AlertCircle, Loader2, Printer, Table2, Pin } from "lucide-react";
import api, { baseURL } from "~/services/api";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import {
  getFilenameFromContentDisposition,
  openBlobInNewTab,
} from "~/utils/blobPreview";

const STORAGE_KEY = "staffs_pinned_filters";
const STORAGE_PINNED_KEY = "staffs_filters_pinned";

const EMPTY_FILTERS = {
  employeeType: "",
  employeeSubtype: "",
  isStudent: "",
  isUsCitizen: "",
  departmentId: "",
  jobDescriptionId: "",
  startDateFrom: "",
  startDateTo: "",
  resignationDateFrom: "",
  resignationDateTo: "",
};

export function getStoredFilters() {
  try {
    const pinned = localStorage.getItem(STORAGE_PINNED_KEY) === "true";
    if (!pinned) return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const Toast = ({ message, show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-[60] animate-in fade-in slide-in-from-top-5 duration-300">
      <div className="bg-red-600 text-white px-6 py-3 rounded-lg shadow-xl flex items-center gap-3">
        <AlertCircle className="w-5 h-5" />
        <span className="font-medium text-sm">{message}</span>
        <button onClick={onClose} className="ml-2 hover:bg-red-700 p-1 rounded-full">
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

const DateInputWithClear = ({ value, onChange, className }) => (
  <div className="relative w-full">
    <input
      type="date"
      className={`${className} pr-10`}
      value={value}
      onChange={onChange}
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange({ target: { value: "" } })}
        className="absolute top-1/2 right-2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 p-1 rounded-full transition-colors bg-white hover:bg-gray-100"
        title="Clear date"
      >
        <X size={14} />
      </button>
    )}
  </div>
);

const SearchableSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder = "Select an option",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(
    options.find((opt) => opt === value)?.toString() ||
      options.find((opt) => opt.value === value)?.label ||
      options.find((opt) => opt.label === value)?.label ||
      value ||
      ""
  );
  const containerRef = useRef(null);

  useEffect(() => {
    const next =
      options.find((opt) => opt === value)?.toString() ||
      options.find((opt) => opt.value === value)?.label ||
      options.find((opt) => opt.label === value)?.label ||
      value ||
      "";
    setQuery(next);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizedOptions = options.map((opt) =>
    typeof opt === "string" ? { value: opt, label: opt } : opt
  );

  const filteredOptions = normalizedOptions.filter((opt) =>
    String(opt.label ?? "").toLowerCase().includes(String(query ?? "").toLowerCase())
  );

  const handleSelect = (option) => {
    setQuery(option.label);
    onChange(option.value);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    onChange(e.target.value);
    setIsOpen(true);
  };

  return (
    <div className="flex flex-col gap-1.5" ref={containerRef}>
      <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
        {label}
      </label>

      <div className="relative">
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500 pr-8"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <ChevronDown
            size={16}
            className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white shadow-xl max-h-48 rounded-md py-1 text-sm overflow-auto border border-gray-200 top-full left-0">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-2 text-gray-500 italic">No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`w-full text-left px-4 py-2 hover:bg-orange-50 flex items-center justify-between ${
                    option.value === value
                      ? "bg-orange-50 text-orange-700 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  {option.label}
                  {option.value === value && <Check size={14} />}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const DEFAULT_OPTIONS = {
  employeeTypes: [
    { value: "Staff", label: "Staff" },
    { value: "Faculty", label: "Faculty" },
  ],
  employeeSubtypes: [
    { value: "Pay Roll", label: "Pay Roll" },
    { value: "Contract", label: "Contract" },
  ],
  yesNo: [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ],
};

export default function ModalFilters({
  opened = false,
  onClose = () => null,
  applying = false,
  onApplyFilters = () => null,
}) {
  const [formData, setFormData] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? { ...EMPTY_FILTERS, ...JSON.parse(raw) } : EMPTY_FILTERS;
    } catch {
      return EMPTY_FILTERS;
    }
  });
  const [pinned, setPinned] = useState(
    () => localStorage.getItem(STORAGE_PINNED_KEY) === "true"
  );

  const [toastState, setToastState] = useState({ show: false, message: "" });
  const [exportingExcel, setExportingExcel] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [jobDescriptions, setJobDescriptions] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [enablePdfOrderBy, setEnablePdfOrderBy] = useState(false);
  const [pdfOrderBy, setPdfOrderBy] = useState("");

  const options = DEFAULT_OPTIONS;

  const inputBaseClasses =
    "w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500";

  const buttonBaseClasses =
    "px-4 py-2 text-sm font-medium border rounded-lg transition-colors shadow-xs flex-1 justify-center items-center flex";
  const secondaryButtonClasses = `${buttonBaseClasses} text-gray-700 bg-white border-gray-300 hover:bg-gray-50`;
  const primaryButtonClasses = `${buttonBaseClasses} text-gray-700 bg-white border-gray-300 hover:bg-gray-50`;
  const clearButtonClasses = "w-full py-2 text-sm font-medium text-red-600 bg-white border border-red-100 rounded-lg hover:bg-red-50 transition-colors mb-1";

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTogglePin = () => {
    const next = !pinned;
    setPinned(next);
    localStorage.setItem(STORAGE_PINNED_KEY, String(next));
    if (!next) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }
  };

  const departmentOptions = useMemo(
    () =>
      departments.map((dept) => ({
        value: dept.id,
        label: dept.name,
      })),
    [departments]
  );

  const jobDescriptionOptions = useMemo(
    () =>
      jobDescriptions.map((job) => ({
        value: job.id,
        label: job.department ? `${job.name} - ${job.department.name}` : job.name,
      })),
    [jobDescriptions]
  );

  useEffect(() => {
    if (!opened) return;
    async function loadDepartments() {
      try {
        const { data } = await api.get(
          `/departments?limit=999&page=1&orderBy=name&orderASC=ASC`
        );
        setDepartments(data.rows || []);
      } catch (err) {
        setDepartments([]);
      }
    }
    loadDepartments();
  }, [opened]);


  useEffect(() => {
    if (!selectedDepartment) {
      setJobDescriptions([]);
      return;
    }
    async function loadJobDescriptionsByDepartment() {
      try {
        const { data } = await api.get(
          `/get-jobs-department/${selectedDepartment}`
        );
        setJobDescriptions(data || []);
      } catch (err) {
        setJobDescriptions([]);
      }
    }
    loadJobDescriptionsByDepartment();
  }, [selectedDepartment]);

  const buildSearchParams = () => {
    const params = new URLSearchParams();
    params.set("limit", "50");
    params.set("page", "1");
    params.set("orderBy", "name");
    params.set("orderASC", "ASC");
    params.set("search", "");
    params.set("type", formData.employeeType || "null");
    params.set("subtype", formData.employeeSubtype || "null");
    if (formData.isStudent !== "") {
      params.set("is_student", formData.isStudent);
    }
    if (formData.isUsCitizen !== "") {
      params.set("is_us_citizen", formData.isUsCitizen);
    }
    if (formData.departmentId) {
      params.set("department_id", formData.departmentId);
    }
    if (formData.jobDescriptionId) {
      params.set("jobdescription_id", formData.jobDescriptionId);
    }
    if (formData.startDateFrom) {
      params.set("admission_date_from", formData.startDateFrom);
    }
    if (formData.startDateTo) {
      params.set("admission_date_to", formData.startDateTo);
    }
    if (formData.resignationDateFrom) {
      params.set("resignation_date_from", formData.resignationDateFrom);
    }
    if (formData.resignationDateTo) {
      params.set("resignation_date_to", formData.resignationDateTo);
    }

    return params;
  };

  const buildQueryString = () => {
    const params = buildSearchParams();
    return `/staffs?${params.toString()}`;
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleApplyFilters = async () => {
    const searchableFields = [
      { key: "employeeType", label: "Group Member", options: options.employeeTypes },
      { key: "employeeSubtype", label: "Employee", options: options.employeeSubtypes },
      { key: "isStudent", label: "Student", options: options.yesNo },
      { key: "isUsCitizen", label: "US Citizen", options: options.yesNo },
      { key: "departmentId", label: "Department", options: departmentOptions },
      { key: "jobDescriptionId", label: "Job Description", options: jobDescriptionOptions },
    ];

    const invalidField = searchableFields.find((field) => {
      const value = formData[field.key];
      if (!value) return false;
      if (Array.isArray(field.options)) {
        return !field.options.some(
          (opt) =>
            (typeof opt === "string" && opt === value) ||
            (typeof opt === "object" && opt.value === value)
        );
      }
      return false;
    });

    if (invalidField) {
      setToastState({
        show: true,
        message: `Option Invalid, choose a option for ${invalidField.label}`
      });
      return;
    }

    if (pinned) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }

    const queryString = buildQueryString();
    console.log("Request preview:", queryString);
    await onApplyFilters(queryString);
    handleClose();
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const params = buildSearchParams();
      const { data } = await api.get(`/staffs/excel?${params.toString()}`);
      const name = data?.name || `staffs_export.xlsx`;
      saveAs(`${baseURL}/get-file/${name}`, name);
      toast("Excel generated successfully!", { type: "success", autoClose: 2500 });
      handleClose();
    } catch (err) {
      console.error("Error exporting excel:", err);
      toast(err?.response?.data?.error || "Failed to generate Excel", { type: "error", autoClose: 3000 });
    } finally {
      setExportingExcel(false);
    }
  };

  const handlePrint = async () => {
    setGeneratingPdf(true);
    try {
      const params = buildSearchParams();
      if (enablePdfOrderBy) {
        if (pdfOrderBy === "department") {
          params.set("order_by_department", "true");
        } else if (pdfOrderBy === "payroll") {
          params.set("order_by_employee_subtype", "payroll");
        } else if (pdfOrderBy === "contract") {
          params.set("order_by_employee_subtype", "contract");
        }
      }
      const response = await api.get(`/staffs/pdf?${params.toString()}`, {
        responseType: "blob",
      });
      const filename =
        getFilenameFromContentDisposition(response.headers["content-disposition"]) ||
        `staffs_${Date.now()}.pdf`;
      const pdfBlob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const opened = openBlobInNewTab(pdfBlob, {
        filename: filename.endsWith(".pdf") ? filename : `${filename}.pdf`,
      });
      if (!opened) {
        toast("Pop-up blocked. Allow pop-ups to preview the PDF.", {
          type: "error",
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      toast(err?.response?.data?.error || "Failed to generate PDF", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (!opened) return null;

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOutRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        .modal-slide-in {
          animation: slideInRight 0.3s ease-out forwards;
        }

        .modal-slide-out {
          animation: slideOutRight 0.3s ease-in forwards;
        }
      `}</style>

      <Toast
        show={toastState.show}
        message={toastState.message}
        onClose={() => setToastState({ ...toastState, show: false })}
      />

      <div
        className="fixed inset-0 z-50 flex justify-end items-center py-6"
        style={{ backgroundColor: "rgba(0,0,0,.75)" }}
        onClick={(event) => {
          if (event.target === event.currentTarget) handleClose();
        }}
      >
        <div className={`bg-white w-full max-w-md h-full flex flex-col shadow-2xl overflow-hidden rounded-l-2xl ${
          isClosing ? 'modal-slide-out' : 'modal-slide-in'
        }`}>

          <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200 bg-white shrink-0">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-tight">Filters</h2>
              <p className="text-xs text-gray-500 font-medium">
                Refine your search results
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleTogglePin}
                title={pinned ? "Unpin filters" : "Pin filters"}
                className={`p-2 rounded-md transition-colors ${pinned ? "bg-orange-100 text-orange-600 hover:bg-orange-200" : "hover:bg-gray-100 text-gray-400 hover:text-gray-600"}`}
              >
                <Pin size={18} className={pinned ? "fill-orange-500" : ""} />
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
                aria-label="Close filters"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 text-sm text-gray-700 custom-scrollbar">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-5">
                <SearchableSelect
                  label="Group Member"
                  options={options.employeeTypes}
                  value={formData.employeeType}
                  onChange={(val) => handleChange("employeeType", val)}
                />

                <SearchableSelect
                  label="Employee"
                  options={options.employeeSubtypes}
                  value={formData.employeeSubtype}
                  onChange={(val) => handleChange("employeeSubtype", val)}
                />

                <SearchableSelect
                  label="Student"
                  options={options.yesNo}
                  value={formData.isStudent}
                  onChange={(val) => handleChange("isStudent", val)}
                />

                <SearchableSelect
                  label="US Citizen"
                  options={options.yesNo}
                  value={formData.isUsCitizen}
                  onChange={(val) => handleChange("isUsCitizen", val)}
                />

                <SearchableSelect
                  label="Department"
                  options={departmentOptions}
                  value={formData.departmentId}
                  onChange={(val) => {
                    handleChange("departmentId", val);
                    setSelectedDepartment(val || "");
                    handleChange("jobDescriptionId", "");
                  }}
                  placeholder="Select a department"
                />

                <SearchableSelect
                  label="Job Description"
                  options={jobDescriptionOptions}
                  value={formData.jobDescriptionId}
                  onChange={(val) => handleChange("jobDescriptionId", val)}
                  placeholder={
                    selectedDepartment
                      ? "Select a job description"
                      : "Select a department first"
                  }
                />

                <div className="pt-4 border-t border-gray-200">
                  <label className="flex items-center gap-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      checked={enablePdfOrderBy}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setEnablePdfOrderBy(next);
                        if (!next) {
                          setPdfOrderBy("");
                        }
                      }}
                    />
                    Order by
                  </label>
                  {enablePdfOrderBy && (
                    <div className="mt-3">
                      <SearchableSelect
                        label="Order By"
                        options={[
                          { value: "department", label: "Department" },
                          { value: "payroll", label: "Subtype: Pay Roll" },
                          { value: "contract", label: "Subtype: Contract" },
                        ]}
                        value={pdfOrderBy}
                        onChange={(val) => setPdfOrderBy(val)}
                        placeholder="Select order"
                      />
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">
                    Start Date Range
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase">
                        From
                      </label>
                      <DateInputWithClear
                        className={inputBaseClasses}
                        value={formData.startDateFrom}
                        onChange={(e) =>
                          handleChange("startDateFrom", e.target.value)
                        }
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase">
                        To
                      </label>
                      <DateInputWithClear
                        className={inputBaseClasses}
                        value={formData.startDateTo}
                        onChange={(e) =>
                          handleChange("startDateTo", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">
                    Resignation Date Range
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase">
                        From
                      </label>
                      <DateInputWithClear
                        className={inputBaseClasses}
                        value={formData.resignationDateFrom}
                        onChange={(e) =>
                          handleChange("resignationDateFrom", e.target.value)
                        }
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase">
                        To
                      </label>
                      <DateInputWithClear
                        className={inputBaseClasses}
                        value={formData.resignationDateTo}
                        onChange={(e) =>
                          handleChange("resignationDateTo", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-6 border-t border-gray-200 bg-gray-50 shrink-0 flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleExportExcel}
                className="py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                disabled={exportingExcel || applying}
              >
                {exportingExcel ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Table2 size={16} />
                    Excel
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handlePrint}
                className="py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                disabled={generatingPdf || applying}
              >
                {generatingPdf ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Printer size={16} />
                    Print
                  </>
                )}
              </button>
            </div>

            <button
              type="button"
              onClick={() => {
                setFormData(EMPTY_FILTERS);
                setEnablePdfOrderBy(false);
                setPdfOrderBy("");
                localStorage.removeItem(STORAGE_KEY);
              }}
              className={clearButtonClasses}
              disabled={applying || exportingExcel}
            >
              Clean All Filters
            </button>

            <div className="flex gap-3 w-full">
              <button
                type="button"
                onClick={handleClose}
                className={secondaryButtonClasses}
                disabled={exportingExcel || applying}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyFilters}
                className={primaryButtonClasses}
                disabled={applying || exportingExcel}
              >
                {applying ? "Applying..." : "Apply Filters"}
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
